import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../api/client';
import { AuthContext } from './AuthContext';
import { formatMoney as format, formatAmount, DEFAULT_CURRENCY } from '../utils/money';

// The company's currency, fetched once for the whole app.
//
// Every screen that showed money used to fetch /settings/defaultCurrency for itself — a
// dozen identical requests on a dashboard, and a dozen chances for one of them to fail and
// leave that panel quietly labelled UGX while the rest of the page said something else.
export const CurrencyContext = createContext({
  currency: DEFAULT_CURRENCY,
  formatMoney: (n, opts) => format(n, DEFAULT_CURRENCY, opts),
  formatAmount
});

export const CurrencyProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);

  useEffect(() => {
    // The settings endpoint requires a session. Fetching before login would spend a
    // guaranteed 401 on every visit to the login page.
    if (!user) return;
    let cancelled = false;
    axios.get('/settings/defaultCurrency')
      .then((res) => {
        if (!cancelled && res.data?.value) setCurrency(res.data.value);
      })
      // The default stands. A missing setting is not worth blocking a screen over.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const formatMoney = useCallback((amount, opts) => format(amount, currency, opts), [currency]);

  const value = useMemo(
    () => ({ currency, formatMoney, formatAmount }),
    [currency, formatMoney]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

/**
 * `const { formatMoney } = useCurrency();`
 *
 * formatMoney(50000) → "UGX 50,000.00"
 * formatMoney(50000, { decimals: 0 }) → "UGX 50,000"
 * formatMoney(50000, { showSymbol: false }) → "50,000.00"
 */
export const useCurrency = () => useContext(CurrencyContext);
